import * as React from "react";
import { Box, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { gaEvent } from "../../analytics";
import { SERVICE_LINKS } from "../../pages/shared/constants";
import { BLUE_PALE } from "../../styles/mui-theme";
import PhoneRepairIcon from "../Icons/services/PhoneRepairIcon";
import ComputerRepairIcon from "../Icons/services/ComputerRepairIcon";
import SecurityCameraIcon from "../Icons/services/SecurityCameraIcon";
import EducationIcon from "../Icons/services/EducationIcon";
import { ServiceIconProps } from "../Icons/services/ServiceIconBase";

// The glyph lives here rather than in `SERVICE_LINKS` so the constants file stays free of React —
// it is a copy file, and every other entry in it is a string.
const ICONS: Record<string, React.FC<ServiceIconProps>> = {
  celulares: PhoneRepairIcon,
  computadores: ComputerRepairIcon,
  camaras: SecurityCameraIcon,
  educacion: EducationIcon,
};

const DISC_SIZE = { xs: 72, sm: 76 };
const GLYPH_SIZE = { xs: 32, sm: 34 };

const ServicesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  // One row of four on every screen, per the wireframe. Wrapping is what a shrink-to-fit row does
  // when the labels are wider than the discs, and "Educación" alone on a second line reads as a
  // fifth, different thing.
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  padding: theme.spacing(3, 1),
  [theme.breakpoints.up("sm")]: {
    gap: theme.spacing(4),
    flexWrap: "wrap",
    padding: theme.spacing(2),
  },
}));

const ServiceLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  // Equal shares of the row on mobile, so four discs fit whatever the label lengths are.
  flex: "1 1 0",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    flex: "0 0 auto",
  },
}));

const IconDisc = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: DISC_SIZE.xs,
  height: DISC_SIZE.xs,
  borderRadius: "50%",
  backgroundColor: BLUE_PALE,
  color: theme.palette.text.primary,
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(1.08)",
  },
  [theme.breakpoints.up("sm")]: {
    width: DISC_SIZE.sm,
    height: DISC_SIZE.sm,
  },
}));

const ServiceLabel = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.primary,
  fontWeight: 500,
  // 12px is what keeps "Computadores" — the longest short label — on one line in the ~84px each
  // tile gets at 375px.
  fontSize: theme.typography.pxToRem(12),
  lineHeight: 1.3,
  maxWidth: "100%",
  [theme.breakpoints.up("sm")]: {
    fontSize: theme.typography.pxToRem(14),
    maxWidth: 120,
  },
}));

export default function Services() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <ServicesContainer>
      {SERVICE_LINKS.map((service) => {
        const Icon = ICONS[service.key];

        return (
          <ServiceLink
            to={service.route}
            key={service.route}
            // Always the full label, never `shortLabel`: the mobile abbreviation is a layout
            // concession, and letting it reach analytics would split one service across two
            // event names.
            onClick={() => gaEvent("service_click", { service_name: service.label })}
          >
            <IconDisc>
              <Icon size={isDesktop ? GLYPH_SIZE.sm : GLYPH_SIZE.xs} />
            </IconDisc>
            <ServiceLabel>{isDesktop ? service.label : service.shortLabel}</ServiceLabel>
          </ServiceLink>
        );
      })}
    </ServicesContainer>
  );
}
